/*
 * A copy of the dispatch console's module of the same name, kept byte-for-byte except for the
 * storage keys and the prose that named the other console.
 *
 * Nothing in it is design content: it is the OpenID Connect authorization-code flow with PKCE, the
 * token refresh, the expiry and the claim reading, all of which both consoles need identically.
 * It is a copy rather than a shared package because this repository builds every package in
 * `packages/` through three explicit steps in the root `package.json`, and a sixth package that
 * `npm run check` could not build before its consumers would be worse than this duplication.
 * `docs/adr/0021-two-consoles-and-no-shared-shell.md` records the decision, what it costs, and the
 * precondition for undoing it.
 */
import { sessionFromClaims, type Session } from "../session.ts";
import type { OidcConfiguration } from "./configuration.ts";
import {
  audienceIncludes,
  authorizationUrl,
  createCodeChallenge,
  createCodeVerifier,
  createState,
  discover,
  endSessionUrl,
  exchangeCode,
  OidcError,
  readClaimsWithoutVerifying,
  refreshTokens,
  type ProviderMetadata,
  type TokenSet,
} from "./oidc.ts";

/**
 * The console's session, from the redirect to the sign-out.
 *
 * One object owns every piece of state the flow has, for a reason that is easy to lose: the question
 * "is this operator signed in" has to have exactly one answer at any moment, and a browser asks it
 * from several places at once — the chrome drawing itself, a board loading, a request about to go out,
 * a timer that woke up. Spreading that across hooks produces a console that is signed out in the
 * header and signed in in the transport.
 *
 * Three decisions are worth reading before the code.
 *
 * **Tokens live in memory and nowhere else.** Not `localStorage`, not `sessionStorage`, not a cookie
 * this script can read. Anything in web storage is readable by any script that gets into the page,
 * and a console loads a font and an icon set from a CDN. The cost is that a reload has no
 * token, which is paid by re-running the authorization request — and because the provider answers on
 * this console's own origin, its session cookie is first-party, so a reload with a live provider
 * session is a redirect that returns immediately rather than a prompt. The only thing written to
 * storage is the in-flight request, which must survive the redirect to be checked when it returns,
 * and which is deleted the moment it is used.
 *
 * **Expiry is handled before a request, not after a refusal.** `accessToken()` is what the transport
 * calls, it is asynchronous, and it refreshes a token that is about to expire before handing it over.
 * So a 401 from expiry is not something the screens have to recover from; it is something that does
 * not happen while a refresh token is valid. An operator half-way through resolving an exception does
 * not lose it because a token aged out under them.
 *
 * **When re-authentication is genuinely impossible, the operator is told rather than redirected.**
 * The design draws that screen — "Your session has ended", in the page body, with the tenant still
 * named — and it is reached only when the refresh grant itself fails. Signing in again from there
 * returns to the page they were on, because the page is in the URL.
 */

/** The states this console can be in with respect to who is using it. */
export type AuthenticationState =
  /** Before anything has been decided: the first paint, and the code exchange on the way back. */
  | { readonly kind: "starting" }
  /**
   * Nobody is signed in.
   *
   * Two facts travel with it, because what the console does next depends on both. `afterSignOut`
   * draws the design's green confirmation banner. `afterAbandonedRequest` says this page load found
   * an authorization request still waiting in storage, so this tab has already been sent to the
   * provider once and has come back without an answer.
   *
   * Neither is a screen's business on its own: `auth/arrival.tsx` turns the pair into the single
   * decision that sits above this state — leave for the provider, or draw a page. The authority
   * itself still asks the provider for nothing here.
   */
  | {
      readonly kind: "signed_out";
      readonly afterSignOut: boolean;
      readonly afterAbandonedRequest: boolean;
    }
  /** Leaving for the provider, or back and exchanging the code. The design's waiting state. */
  | { readonly kind: "authenticating" }
  | { readonly kind: "failed"; readonly failure: SignInFailure }
  | { readonly kind: "signed_in"; readonly session: Session }
  /**
   * Signed in, and the session cannot be renewed. Carries what the design's card states: the tenant,
   * the time it ended, and how long it had lasted.
   */
  | {
      readonly kind: "expired";
      readonly session: Session;
      readonly endedAt: number;
      readonly lastedMs: number;
    };

/**
 * A sign-in that did not finish, in the words the screen shows.
 *
 * The design draws exactly one failure — an identity the provider confirmed and the platform has no
 * account for — and that one is reproduced verbatim. The others are written in its grammar: what
 * happened, what is true now, and whether retrying helps. Recorded as Q-17.
 */
export interface SignInFailure {
  /** The provider's own error name where there was one, otherwise one of ours. */
  readonly code: string;
  readonly headline: string;
  readonly detail: string;
  /** Whether the screen offers "Use a different account" or just lets them try again. */
  readonly retryable: boolean;
}

export interface AuthorityEnvironment {
  readonly configuration: OidcConfiguration;
  readonly fetch: typeof globalThis.fetch;
  readonly crypto: Pick<Crypto, "getRandomValues" | "subtle">;
  readonly storage: Pick<Storage, "getItem" | "setItem" | "removeItem">;
  /** The whole current URL, including the query the provider redirected back with. */
  readonly currentUrl: () => string;
  /** Rewrite the address bar without navigating. Used to take the code back out of the URL. */
  readonly replaceUrl: (url: string) => void;
  /** Leave this page. Used for the two redirects a browser flow is made of. */
  readonly navigate: (url: string) => void;
  readonly now: () => number;
}

/**
 * How close to expiry counts as expired.
 *
 * Thirty seconds, which is the time a request needs rather than a round number: a token that expires
 * while a listing is in flight is refused by the service even though it was valid when the console
 * decided to send it. The window has to cover the slowest request this console makes, not the
 * fastest.
 */
const EXPIRY_SKEW_MS = 30_000;

const REQUEST_KEY = "tdelivr.tenant-admin-console.authorization-request";
const SIGNED_OUT_KEY = "tdelivr.tenant-admin-console.signed-out";

interface PendingRequest {
  readonly state: string;
  readonly codeVerifier: string;
  /** The URL the operator was on, so sign-in returns them there rather than to the default page. */
  readonly returnTo: string;
}

function isPendingRequest(value: unknown): value is PendingRequest {
  if (value === null || typeof value !== "object") return false;
  const candidate = value as Partial<PendingRequest>;
  return (
    typeof candidate.state === "string" &&
    typeof candidate.codeVerifier === "string" &&
    typeof candidate.returnTo === "string"
  );
}

/**
 * The tenant's name, where the token carries one.
 *
 * `tenant_id` is the claim ADR 0003 and ADR 0014 settle, and it is a uuid. The chrome has to state
 * the tenant at all times and a uuid is not a name, so an optional `tenant_name` claim is read when
 * the realm supplies one and the identifier is shown when it does not. Nothing is invented: a
 * console naming a tenant it was not told about would be the one thing this shell must never get
 * wrong. TEN-01 is the proper source and publishes no payload at all — see R-07 and R-11.
 */
function tenantNameFrom(claims: Record<string, unknown>): string {
  const name = claims.tenant_name;
  if (typeof name === "string" && name.trim().length > 0) return name.trim();
  return typeof claims.tenant_id === "string" ? claims.tenant_id : "Unknown tenant";
}

/**
 * Turn whatever went wrong into the words the sign-in screen shows.
 *
 * Exhaustive over the cases rather than defaulted, so a new failure has to be given a sentence
 * instead of inheriting one that is wrong about whether retrying helps.
 */
function failureFor(code: string, providerName: string, detail: string): SignInFailure {
  switch (code) {
    case "claims_incomplete":
      // The design's own failure, word for word, with the provider named as it names it. This is the
      // case ADR 0014 calls security-critical: a token issued without `tenant_id` reaches every
      // service as an actor with no tenant.
      return {
        code,
        headline: "Your company account isn't linked to this platform yet",
        detail: `${providerName} confirmed who you are, but no platform account is linked to that identity. A tenant administrator links it in Staff and roles. Retrying won't change this.`,
        retryable: false,
      };
    case "wrong_audience":
      return {
        code,
        headline: "This sign-in is not for the dispatch platform",
        detail: `${providerName} issued a token for a different audience, so every page in this console would be refused. A platform administrator fixes this in the realm. Retrying won't change this.`,
        retryable: false,
      };
    case "access_denied":
    case "login_required":
    case "interaction_required":
      return {
        code,
        headline: "Sign-in was not completed",
        detail: `${providerName} did not finish signing you in. Nothing was created. You can try again.`,
        retryable: true,
      };
    case "unmatched_callback":
      return {
        code,
        headline: "This sign-in did not come from this console",
        detail:
          "The answer that arrived does not match a request this page started, so it was discarded. Nothing was created. Start again from here.",
        retryable: true,
      };
    case "provider_unreachable":
      return {
        code,
        headline: `The console cannot reach ${providerName}`,
        detail: `${detail} Nothing was created. You can try again.`,
        retryable: true,
      };
    case "provider_issuer_mismatch":
    case "provider_metadata_incomplete":
      return {
        code,
        headline: "This console is pointed at the wrong identity provider",
        detail: `${detail} A platform administrator fixes this in the console's configuration. Retrying won't change this.`,
        retryable: false,
      };
    default:
      return {
        code,
        headline: "Sign-in didn't complete",
        detail: `${providerName} refused the request: ${detail} Nothing was created. You can try again.`,
        retryable: true,
      };
  }
}

export class ConsoleAuthority {
  private state: AuthenticationState = { kind: "starting" };
  private readonly listeners = new Set<() => void>();
  private tokens: TokenSet | undefined;
  private metadata: ProviderMetadata | undefined;
  private signedInAt = 0;
  /** One refresh at a time. Four screens polling at once must not become four refresh grants. */
  private refreshing: Promise<TokenSet | undefined> | undefined;

  public constructor(private readonly environment: AuthorityEnvironment) {}

  public getState(): AuthenticationState {
    return this.state;
  }

  /** What the sign-in screen calls the provider, so a component need not carry the configuration. */
  public get providerName(): string {
    return this.environment.configuration.providerName;
  }

  public subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private publish(state: AuthenticationState): void {
    this.state = state;
    for (const listener of this.listeners) listener();
  }

  /**
   * Decide what this page load is: a return from the provider, a return from a sign-out, or a fresh
   * arrival.
   *
   * Called once. Everything else in this class is driven by the operator or by a token ageing.
   */
  public async start(): Promise<void> {
    const url = new URL(this.environment.currentUrl());
    const code = url.searchParams.get("code");
    const error = url.searchParams.get("error");

    if (code === null && error === null) {
      const afterSignOut = this.environment.storage.getItem(SIGNED_OUT_KEY) !== null;
      if (afterSignOut) this.environment.storage.removeItem(SIGNED_OUT_KEY);
      /*
       * A request left in storage is read, and deliberately left where it is.
       *
       * Reaching here with one means this tab went to the provider and came back carrying neither a
       * code nor an error — the operator pressed Back, or the provider returned to the redirect URI
       * without answering. Since ADR 0022 an arrival with nothing to report leaves for the provider
       * on its own, so that is precisely the arrival that must not: it would go straight back out
       * and return the same way, for ever. The request is not cleared here, so the latch holds
       * across a reload too; only a real callback or a new `beginSignIn` replaces it.
       */
      this.publish({
        kind: "signed_out",
        afterSignOut,
        afterAbandonedRequest: this.environment.storage.getItem(REQUEST_KEY) !== null,
      });
      return;
    }

    this.publish({ kind: "authenticating" });

    const pending = this.takePendingRequest();
    const returnTo = pending?.returnTo ?? this.withoutCallbackParameters(url);

    /*
     * The provider's answer leaves the address bar before anything is done with it.
     *
     * An authorization code is good for one exchange, so a bar still holding one is a reload away
     * from a second attempt that the provider will refuse — and the operator would be shown
     * `invalid_grant` for a sign-in that actually worked.
     */
    this.environment.replaceUrl(returnTo);

    if (error !== null) {
      this.fail(error, url.searchParams.get("error_description") ?? "No reason was given.");
      return;
    }

    /*
     * The state check is the one thing here that is a security control rather than plumbing.
     *
     * Without it this console would exchange any code anybody could get it to load, which is the
     * cross-site request forgery the parameter exists to stop. A missing pending request is the same
     * failure as a mismatched one — both mean the answer did not come from a request this page made,
     * and so is an answer carrying neither a code nor an error.
     */
    if (code === null || pending === undefined || pending.state !== url.searchParams.get("state")) {
      this.fail("unmatched_callback", "");
      return;
    }

    try {
      const metadata = await this.providerMetadata();
      const tokens = await exchangeCode(
        {
          metadata,
          clientId: this.environment.configuration.clientId,
          redirectUri: this.environment.configuration.redirectUri,
          code,
          codeVerifier: pending.codeVerifier,
        },
        this.environment.fetch,
        this.environment.now(),
      );
      this.adopt(tokens);
    } catch (cause) {
      this.fail(
        cause instanceof OidcError ? cause.code : "sign_in_failed",
        cause instanceof Error ? cause.message : "",
      );
    }
  }

  /**
   * Take the claims out of a token and become that operator, or refuse the token.
   *
   * Shared by the first sign-in and every refresh, deliberately. A refresh that produced a token for
   * a different subject or a different tenant would otherwise be adopted silently, and the chrome
   * would go on naming the old one.
   */
  private adopt(tokens: TokenSet): void {
    const claims = readClaimsWithoutVerifying(tokens.accessToken);
    const { audience } = this.environment.configuration;
    if (audience !== undefined && !audienceIncludes(claims, audience)) {
      this.fail("wrong_audience", "");
      return;
    }

    const session = sessionFromClaims(claims, tenantNameFrom(claims));
    if (session === undefined) {
      this.fail("claims_incomplete", "");
      return;
    }

    this.tokens = tokens;
    if (this.signedInAt === 0) this.signedInAt = this.environment.now();
    this.publish({ kind: "signed_in", session });
  }

  private fail(code: string, detail: string): void {
    this.tokens = undefined;
    this.signedInAt = 0;
    this.publish({
      kind: "failed",
      failure: failureFor(code, this.environment.configuration.providerName, detail),
    });
  }

  /**
   * Leave for the provider.
   *
   * Everything here is PKCE: a verifier that stays in this browser, its hash that does not, and a
   * state that ties the answer back to this request.
   *
   * `loginHint` is the standard `login_hint` (OpenID Connect Core 3.1.2.1) and no screen supplies
   * one any more. The console's own email step was where it came from, and ADR 0022 removed that
   * step because the provider's own form asks for the same address. The parameter stays because it
   * is the protocol's, not the screen's: a caller that knows which operator is signing in — a
   * re-authentication, an invitation link — still has somewhere to put it.
   */
  public async beginSignIn(loginHint?: string): Promise<void> {
    this.publish({ kind: "authenticating" });
    try {
      const metadata = await this.providerMetadata();
      const codeVerifier = createCodeVerifier(this.environment.crypto);
      const state = createState(this.environment.crypto);
      const codeChallenge = await createCodeChallenge(codeVerifier, this.environment.crypto);

      this.environment.storage.setItem(
        REQUEST_KEY,
        JSON.stringify({
          state,
          codeVerifier,
          returnTo: this.environment.currentUrl(),
        } satisfies PendingRequest),
      );

      this.environment.navigate(
        authorizationUrl({
          metadata,
          clientId: this.environment.configuration.clientId,
          redirectUri: this.environment.configuration.redirectUri,
          scopes: this.environment.configuration.scopes,
          state,
          codeChallenge,
          ...(loginHint === undefined || loginHint.length === 0 ? {} : { loginHint }),
          ...(this.environment.configuration.audience === undefined
            ? {}
            : { audience: this.environment.configuration.audience }),
        }),
      );
    } catch (cause) {
      this.fail(
        cause instanceof OidcError ? cause.code : "sign_in_failed",
        cause instanceof Error ? cause.message : "",
      );
    }
  }

  /**
   * The token for the next request, refreshed first if it is about to expire.
   *
   * This is what `createHttpTransport`'s `authorization` callback is given, and it is the reason a
   * silent 401 is not a state the screens have to handle. The transport already accepts a promise
   * here, so nothing in `packages/api-client` had to change.
   */
  public async accessToken(): Promise<string | undefined> {
    const tokens = this.tokens;
    if (tokens === undefined) return undefined;
    if (tokens.expiresAt - this.environment.now() > EXPIRY_SKEW_MS) return tokens.accessToken;

    const refreshed = await this.refresh();
    return refreshed?.accessToken;
  }

  /**
   * How long until this token needs renewing, for a caller that wants to renew it before a request
   * needs it. Undefined when there is nothing to renew.
   */
  public millisecondsUntilRenewal(): number | undefined {
    if (this.tokens === undefined) return undefined;
    return Math.max(0, this.tokens.expiresAt - this.environment.now() - EXPIRY_SKEW_MS);
  }

  /** Renew now, whether or not the current token has aged. Used by the renewal timer. */
  public async renew(): Promise<void> {
    if (this.tokens === undefined) return;
    await this.refresh();
  }

  private async refresh(): Promise<TokenSet | undefined> {
    this.refreshing ??= this.refreshOnce().finally(() => {
      this.refreshing = undefined;
    });
    return this.refreshing;
  }

  private async refreshOnce(): Promise<TokenSet | undefined> {
    const current = this.tokens;
    const refreshToken = current?.refreshToken;
    if (current === undefined) return undefined;
    if (refreshToken === undefined) {
      this.expire();
      return undefined;
    }

    try {
      const tokens = await refreshTokens(
        {
          metadata: await this.providerMetadata(),
          clientId: this.environment.configuration.clientId,
          refreshToken,
        },
        this.environment.fetch,
        this.environment.now(),
      );
      this.adopt(tokens);
      // `adopt` refuses a token whose claims no longer make a session, and in that case it has
      // already published a failure. Returning the tokens anyway would hand the transport a token
      // this console has just rejected.
      return this.state.kind === "signed_in" ? tokens : undefined;
    } catch (cause) {
      /*
       * A refresh that fails because the grant is gone is the end of the session. A refresh that
       * fails because the provider is unreachable is not — the token may still be good, the network
       * is not, and signing the operator out of a console they are working in because a depot lost
       * its uplink for ten seconds would be the console inventing an expiry.
       */
      if (cause instanceof OidcError && cause.code === "provider_unreachable") return current;
      this.expire();
      return undefined;
    }
  }

  private expire(): void {
    if (this.state.kind !== "signed_in") return;
    const endedAt = this.environment.now();
    this.publish({
      kind: "expired",
      session: this.state.session,
      endedAt,
      lastedMs: this.signedInAt === 0 ? 0 : endedAt - this.signedInAt,
    });
    this.tokens = undefined;
  }

  /**
   * End the provider's session as well as this one.
   *
   * Dropping the tokens alone would leave the operator signed in at the provider, so the next
   * sign-in would complete without asking for anything — wrong on any machine and dangerous on a
   * shared dispatch workstation, which is what the design's dialog means by "all consoles on this
   * device".
   */
  public async signOut(): Promise<void> {
    const idToken = this.tokens?.idToken;
    this.tokens = undefined;
    this.signedInAt = 0;
    this.environment.storage.setItem(SIGNED_OUT_KEY, "1");
    this.environment.storage.removeItem(REQUEST_KEY);

    let goodbye: string | undefined;
    try {
      goodbye = endSessionUrl({
        metadata: await this.providerMetadata(),
        clientId: this.environment.configuration.clientId,
        postLogoutRedirectUri: this.environment.configuration.redirectUri,
        ...(idToken === undefined ? {} : { idToken }),
      });
    } catch {
      // The provider is unreachable. The local session is already gone, which is the half of signing
      // out this console can guarantee, and the operator is told so rather than left on a page whose
      // token has been thrown away.
      goodbye = undefined;
    }

    if (goodbye === undefined) {
      this.environment.storage.removeItem(SIGNED_OUT_KEY);
      // No abandoned request: this method has just removed it. The banner is the point of this
      // state, and `arrivalFor` keeps the operator on it rather than signing them straight back in.
      this.publish({ kind: "signed_out", afterSignOut: true, afterAbandonedRequest: false });
      return;
    }
    this.environment.navigate(goodbye);
  }

  private async providerMetadata(): Promise<ProviderMetadata> {
    this.metadata ??= await discover(this.environment.configuration.issuer, this.environment.fetch);
    return this.metadata;
  }

  private takePendingRequest(): PendingRequest | undefined {
    const raw = this.environment.storage.getItem(REQUEST_KEY);
    // Removed whether or not it parses. A request is good for exactly one answer, and one left behind
    // is one a later callback could be matched against.
    this.environment.storage.removeItem(REQUEST_KEY);
    if (raw === null) return undefined;
    try {
      const parsed = JSON.parse(raw) as unknown;
      return isPendingRequest(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  }

  private withoutCallbackParameters(url: URL): string {
    const clean = new URL(url.toString());
    for (const parameter of [
      "code",
      "state",
      "error",
      "error_description",
      "session_state",
      "iss",
    ]) {
      clean.searchParams.delete(parameter);
    }
    return clean.toString();
  }
}
